export interface LoginAuthSettings {
  /** When true, only email addresses are accepted (no phone / OTP). */
  emailOnly: boolean;
  /** Lowercase email addresses as the user types and before API calls. */
  lowercaseEmail: boolean;
  usernameLabel: string;
  usernamePlaceholder: string;
  emptyUsernameMessage: string;
  invalidEmailMessage: string;
}

const PARTNER_LOGIN: LoginAuthSettings = {
  emailOnly: true,
  lowercaseEmail: true,
  usernameLabel: 'Enter your email address',
  usernamePlaceholder: 'Email address',
  emptyUsernameMessage: 'Please enter your email address',
  invalidEmailMessage: 'Please enter a valid email address',
};

const CUSTOMER_LOGIN: LoginAuthSettings = {
  emailOnly: false,
  lowercaseEmail: true,
  usernameLabel: 'Enter your e-mail address / mobile number',
  usernamePlaceholder: 'Email or Mobile Number',
  emptyUsernameMessage: 'Please enter your email or mobile number',
  invalidEmailMessage: 'Please enter a valid email address',
};

export function getLoginAuthSettings(isPartners: boolean): LoginAuthSettings {
  return isPartners ? PARTNER_LOGIN : CUSTOMER_LOGIN;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function shouldLowercaseUsername(value: string, settings: LoginAuthSettings): boolean {
  if (!settings.lowercaseEmail) {
    return false;
  }
  if (settings.emailOnly) {
    return true;
  }
  return value.includes('@');
}

export function formatUsernameInput(value: string, settings: LoginAuthSettings): string {
  if (shouldLowercaseUsername(value, settings)) {
    return value.toLowerCase();
  }
  return value;
}

export function normalizeUsernameForSubmit(value: string, settings: LoginAuthSettings): string {
  const trimmed = value.trim();
  return formatUsernameInput(trimmed, settings);
}

export function validateUsername(value: string, settings: LoginAuthSettings): string | null {
  const normalized = normalizeUsernameForSubmit(value, settings);
  if (!normalized) {
    return settings.emptyUsernameMessage;
  }
  if (settings.emailOnly && !EMAIL_PATTERN.test(normalized)) {
    return settings.invalidEmailMessage;
  }
  return null;
}

export function isEmailUsername(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** HTML autocomplete tokens for login forms (password manager + browser autofill). */
export const LOGIN_FORM_AUTOCOMPLETE = {
  username: 'username',
  currentPassword: 'current-password',
} as const;
